import {
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from 'recharts'
import {
    KRDS,
    CHART_GRID,
    CHART_AXIS,
    tooltipStyles,
} from '~/components/charts/chart-colors'

export interface CategoryDatum {
    name: string
    value: number
    color?: string
}

interface CategoryBarChartProps {
    data: CategoryDatum[]
    /** 'vertical' = 가로 막대(상태 파이프라인 등), 'horizontal' = 세로 막대(서비스 비교) */
    layout?: 'horizontal' | 'vertical'
    color?: string
    height?: number
    unit?: string
}

// 범주형 비교 막대. 가로/세로 전환, 막대별 색(color) 지정 가능. KRDS 격자·축.
export function CategoryBarChart({
    data,
    layout = 'horizontal',
    color = KRDS.primary,
    height = 220,
    unit = '건',
}: CategoryBarChartProps) {
    const isVertical = layout === 'vertical'

    return (
        // 고정 높이 래퍼 + height='100%'로 감싸 ResponsiveContainer가 스크롤 컨테이너에
        // 팬텀 하단 높이를 만드는 recharts 이슈를 막는다(width='100%'+숫자높이 조합 회피).
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                    data={data}
                    layout={layout}
                    margin={{
                        top: 8,
                        right: isVertical ? 36 : 8,
                        bottom: 0,
                        left: 0,
                    }}
                    barCategoryGap={isVertical ? '28%' : '24%'}>
                    <CartesianGrid
                        stroke={CHART_GRID}
                        horizontal={!isVertical}
                        vertical={isVertical}
                    />
                    {isVertical ? (
                        <>
                            <XAxis
                                type='number'
                                tick={{ fill: CHART_AXIS, fontSize: 12 }}
                                axisLine={{ stroke: CHART_GRID }}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <YAxis
                                type='category'
                                dataKey='name'
                                width={88}
                                tick={{ fill: KRDS.gray[80], fontSize: 13 }}
                                axisLine={false}
                                tickLine={false}
                            />
                        </>
                    ) : (
                        <>
                            <XAxis
                                dataKey='name'
                                tick={{ fill: KRDS.gray[80], fontSize: 13 }}
                                axisLine={{ stroke: CHART_GRID }}
                                tickLine={false}
                            />
                            <YAxis
                                type='number'
                                tick={{ fill: CHART_AXIS, fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                        </>
                    )}
                    <Tooltip
                        formatter={(v) => [`${v ?? 0}${unit}`, '']}
                        contentStyle={tooltipStyles.contentStyle}
                        itemStyle={tooltipStyles.itemStyle}
                        labelStyle={tooltipStyles.labelStyle}
                        cursor={tooltipStyles.cursor}
                    />
                    <Bar
                        dataKey='value'
                        fill={color}
                        radius={isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                        maxBarSize={isVertical ? 26 : 56}
                        isAnimationActive={false}>
                        {data.map((d, i) => (
                            <Cell key={i} fill={d.color ?? color} />
                        ))}
                        <LabelList
                            dataKey='value'
                            position={isVertical ? 'right' : 'top'}
                            style={{ fill: KRDS.gray[60], fontSize: 12 }}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
